// auth.js
import { API_ENDPOINTS } from '../utils/constants.js';

const isSafari = () => {
  const userAgent = navigator.userAgent;
  return (
    /^((?!chrome|android).)*safari/i.test(userAgent) ||
    /iPad|iPhone|iPod/.test(userAgent)
  );
};

const persistentStorage = {
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      if (isSafari()) {
        sessionStorage.setItem(key, value);
        localStorage.setItem(`${key}_safari_flag`, Date.now().toString());
      }
    } catch (error) {
      console.warn('Storage failed:', error);
      sessionStorage.setItem(key, value);
    }
  },

  getItem: key => {
    try {
      let value = localStorage.getItem(key);
      if (!value && isSafari()) {
        value = sessionStorage.getItem(key);
        if (value) {
          localStorage.setItem(key, value);
        }
      }
      return value;
    } catch (error) {
      console.warn('Storage read failed:', error);
      return sessionStorage.getItem(key);
    }
  },

  removeItem: key => {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
      if (isSafari()) {
        localStorage.removeItem(`${key}_safari_flag`);
      }
    } catch (error) {
      console.warn('Storage removal failed:', error);
    }
  },
};

export const AuthService = {
  _refreshPromise: null,
  _tokenExpiryTime: null, // Track token expiry

  getAccessToken: () => persistentStorage.getItem('tringAuthToken'),

  setAccessToken: (token, expiresIn = 3600) => {
    if (token) {
      persistentStorage.setItem('tringAuthToken', token);
      AuthService._tokenExpiryTime = Date.now() + expiresIn * 1000;
      persistentStorage.setItem(
        'tringTokenExpiry',
        AuthService._tokenExpiryTime.toString()
      );

      if (isSafari()) {
        const safariExpiry = Date.now() + expiresIn * 1000 * 2;
        persistentStorage.setItem(
          'tringTokenExpiry_safari',
          safariExpiry.toString()
        );
      }
    } else {
      persistentStorage.removeItem('tringAuthToken');
      persistentStorage.removeItem('tringTokenExpiry');
      persistentStorage.removeItem('tringTokenExpiry_safari');
      AuthService._tokenExpiryTime = null;
    }
  },

  getRefreshToken: () => persistentStorage.getItem('tringRefreshToken'),

  setRefreshToken: token => {
    if (token) {
      persistentStorage.setItem('tringRefreshToken', token);
    } else {
      persistentStorage.removeItem('tringRefreshToken');
    }
  },

  isTokenExpired: () => {
    const expiryTime = persistentStorage.getItem('tringTokenExpiry');
    if (!expiryTime) return true;

    const expiry = parseInt(expiryTime, 10);
    AuthService._tokenExpiryTime = expiry;
    if (isSafari()) {
      const safariFlag = persistentStorage.getItem(
        'tringAuthToken_safari_flag'
      );
      const safariExpiry = persistentStorage.getItem('tringTokenExpiry_safari');

      if (!safariFlag && safariExpiry) {
        return Date.now() >= parseInt(safariExpiry, 10);
      }
    }

    return Date.now() >= expiry;
  },

  clearTokens: () => {
    // Clear all possible token locations
    persistentStorage.removeItem('tringAuthToken');
    persistentStorage.removeItem('tringTokenExpiry');
    persistentStorage.removeItem('tringTokenExpiry_safari');
    persistentStorage.removeItem('tringRefreshToken');
    persistentStorage.removeItem('userId');

    // Add manual clearing as safeguard
    localStorage.removeItem('tringAuthToken');
    sessionStorage.removeItem('tringAuthToken');

    AuthService._tokenExpiryTime = null;
    AuthService._refreshPromise = null;

    console.log('All tokens cleared'); // Debug confirmation
  },

  // Secure refresh token with rotation
  refreshToken: async () => {
    if (AuthService._refreshPromise) {
      return await AuthService._refreshPromise;
    }

    // Create the refresh promise
    AuthService._refreshPromise = (async () => {
      try {
        const currentRefreshToken = AuthService.getRefreshToken();

        if (!currentRefreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await fetch(API_ENDPOINTS.AUTH_REFRESH, {
          method: 'POST',
          headers: {
            'X-Platform': 'browser',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            refreshToken: currentRefreshToken,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          AuthService.clearTokens();
          throw new Error(
            `Refresh failed: ${errorData.message || response.statusText}`
          );
        }
        const data = await response.json();
        AuthService.setAccessToken(data.accessToken, data.expiresIn);
        if (data.refreshToken) {
          AuthService.setRefreshToken(data.refreshToken);
        }

        return data.accessToken;
      } catch (error) {
        AuthService.clearTokens();
        throw error;
      } finally {
        AuthService._refreshPromise = null;
      }
    })();

    return await AuthService._refreshPromise;
  },
};

export const fetchWithAuth = async (url, options = {}) => {
  const requestId = Math.random().toString(36).substr(2, 9);

  if (AuthService.isTokenExpired()) {
    await AuthService.refreshToken();
  }

  const authToken = AuthService.getAccessToken();

  if (!authToken) {
    throw new Error('No access token available');
  }

  let response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
  });

  // If 401 Unauthorized, try refreshing token once
  if (response.status === 401) {
    try {
      const newToken = await AuthService.refreshToken();
      const retryOptions = {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${newToken}`,
          'x-platform': 'browser',
          'Content-Type': 'application/json',
        },
      };

      if (options.body) {
        retryOptions.body = options.body;
      }

      response = await fetch(url, retryOptions);
      console.log(
        `✅ [${requestId}] Request retried successfully after refresh`
      );
    } catch (refreshError) {
      console.error(`❌ [${requestId}] Token refresh failed:`, refreshError);
      AuthService.clearTokens();
      throw refreshError;
    }
  }

  return response;
};
